import { Fragment, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Box, TableBody, TableCell, TableHead, TableRow, Stack, Tooltip, IconButton } from '@mui/material'
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff'
import { FormProvider, useForm } from 'react-hook-form'
import dayjs from 'dayjs'
import Papa from 'papaparse'

// components
import { StickyTable } from '../common/StickyTable'
import { CustomTableCell } from '../common/CustomTableCell'
import { CardBox } from '../../theme/styleComponents'
import { CustomPagination } from '../common/CustomPagination'
import { ContentHead } from '../common/ContentHead'
import { ReportsMenu } from './ReportsMenu'
import DateRangeFilter from '../common/DateRangeFilter'
import { ReportWidget } from './ReportWidget'
import {
  FetchReportsQuery,
  ReportFileType,
  ReportGroup,
  ReportStatus,
  Report,
  useDownloadReportLazyQuery,
  useFetchReportsLazyQuery,
  useGetReportTemplatesQuery,
  useRetryReportMutation,
  useCancelReportMutation
} from '../../../generated'
import { GRAPHQL_QUERY_POLICY, QUERY_PARAMS, REPORTS_PAGE_LIMIT } from '../../constants'
import { AppContext } from '../../context'
import { AuthContext } from '../../context/AuthContext'
import { ReportCreatorSelect } from './ReportCreatorSelect'
import { statusColors } from '../../utils/billing'
import { formatDateTime, isReportsRestrictedUser } from '../../utils'
import { flexCenter } from '../../theme/styleConstants'
import { EmptyData } from '../common/EmptyData'
import { ActionType } from '../../context/AppContextReducer'
import { OverlayLoader } from '../common/OverlayLoader'
import { Alert } from '../common/Alert'
import { ConfirmationModal } from '../common/ConfirmationModal'
import { ErrorException, ReportPreviewData } from '../../interfaceTypes'
import { ApolloError } from '@apollo/client'
import { useSearchParams } from 'react-router-dom'
import { ReportSelectionSelect } from './ReportSelectionSelect'
import { ReportGroupSelect } from './ReportGroupSelect'
import ReportPreviewDialog from './ReportPreviewDialog'
import {
  formatForBalanceSheet,
  formatForIncomeStatement,
  formatForTrialBalance,
  getSelectionFieldEntries
} from '../../utils/reports'

const POLL_INTERVAL_MS = 20_000

const REPORT_HEADERS = [
  'Date / Time',
  'Generated By',
  'Report Group',
  'Report Selection',
  'Selection Fields',
  'Status',
  'Details',
  'Action'
]

const formatSelectionFields = (
  selectionFields: string | undefined,
  reportGroup: string | undefined,
  reportSelection: string | undefined
): string => {
  if (!selectionFields || !reportGroup || !reportSelection) return ''

  const fields = selectionFields as unknown as Record<string, any>
  const reportType = reportSelection?.toLowerCase()

  switch (reportType) {
    case 'balancesheet':
      return formatForBalanceSheet(fields)
    case 'incomestatement':
      return formatForIncomeStatement(fields)
    case 'trialbalance':
      return formatForTrialBalance(fields)
    default:
      return ''
  }
}

const Reports = () => {
  const methods = useForm()
  const [previewReport, setPreviewReport] = useState(false)
  const [selectedReport, setSelectedReport] = useState<Report | null>(null)
  const [reportJsonData, setReportJsonData] = useState<ReportPreviewData[]>([])
  const { dispatch: reducerDispatch, currentPage } = useContext(AppContext)
  const { currentUser } = useContext(AuthContext)
  const [reports, setReports] = useState<FetchReportsQuery['fetchReports']['reports'] | null>([])
  const [searchParams, setSearchParams] = useSearchParams()
  const [reportTemplates, setReportTemplates] = useState<ReportGroup[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const reportGroup = searchParams.get('reportGroup')
  const reportSelection = searchParams.get('reportSelection')
  const userParam = searchParams.get(QUERY_PARAMS.USER)

  const isRestricted = isReportsRestrictedUser(currentUser)

  // Everyone lands on their own reports; picking "All" (or another person) in the
  // Generated By filter widens it. `userParam === null` means the URL has not been seeded
  // yet — distinct from the explicit 'all', which must NOT fall back to the current user.
  //
  // A restricted user (any role but super-admin/admin/accountant) cannot widen it at all: the filter is
  // locked for them and the value is pinned
  // to their own id, so a hand-edited `?user=` in the URL changes nothing. The API pins it
  // again on its side — this only keeps the page from asking for something it will not get.
  const selectedCreator = userParam === null ? currentUser?.id : userParam
  const creatorId = isRestricted ? currentUser?.id : selectedCreator
  const isAllUsers = !isRestricted && creatorId === QUERY_PARAMS.ALL

  // Seed the URL with the logged-in user once they are loaded, so the filter shows who it
  // is filtering by and a page refresh keeps the same view. `replace` avoids pushing a
  // history entry the user never asked for.
  useEffect(() => {
    if (userParam === null && currentUser?.id) {
      searchParams.set(QUERY_PARAMS.USER, currentUser.id)
      setSearchParams(searchParams, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id, userParam])

  // The API stores the raw selection value (e.g. "timeKeeperHoursAnalysisMatterDetail").
  // Map it back to the label the user actually picked in the dropdown
  // ("Timekeeper Analysis Matter Detail/PR4A") so the history table matches.
  // Labels in the templates carry trailing spaces, hence the trim.
  const selectionLabelsByValue = useMemo(() => {
    const labels: Record<string, string> = {}
    reportTemplates?.forEach(group =>
      group?.selections?.forEach(selection => {
        const value = selection?.selection
        const label = selection?.label?.trim()
        if (value && label) labels[value] = label
      })
    )
    return labels
  }, [reportTemplates])
  const startDate = searchParams.get('startDate')
  const endDate = searchParams.get('endDate')

  const handleDateChange = (newFromDate: string | null, newToDate: string | null) => {
    newFromDate && handleChangeSearch('startDate', newFromDate)
    newToDate && handleChangeSearch('endDate', newToDate)
  }

  const handleGraphQLError = (error: ApolloError) => {
    reducerDispatch({ type: ActionType.SET_IS_LOADING, isLoading: false })
    const { graphQLErrors } = error

    if (graphQLErrors) {
      graphQLErrors.map(
        // eslint-disable-next-line
        ({ extensions }) => {
          if (extensions) {
            const { exception } = extensions
            if (exception) {
              const { response } = exception as ErrorException
              const { response: errorResponse } = response || {}
              if (errorResponse) {
                const { error } = errorResponse
                Alert.error(error)
              } else {
                Alert.error('Something went wrong')
              }
            }
          }
        }
      )
    }
  }

  useGetReportTemplatesQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,

    onCompleted(data) {
      setReportTemplates(data?.getReportTemplates || [])
    }
  })

  const [fetchReports, { loading: fetchReportsLoading, refetch: refetchReports }] = useFetchReportsLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,

    onError() {
      reducerDispatch({ type: ActionType.SET_IS_LOADING, isLoading: false })
    },

    onCompleted(data) {
      const {
        fetchReports: { reports, pagination }
      } = data || {}

      if (data?.fetchReports) {
        setReports(reports || [])
      }

      if (pagination) {
        const { totalPages } = pagination

        totalPages && reducerDispatch({ type: ActionType.SET_TOTAL_PAGE_COUNT, totalPageCount: totalPages })
      }
    }
  })

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
    const hasInProgress = reports?.some(r => r?.status === ReportStatus.InProgress)
    if (hasInProgress) {
      pollRef.current = setInterval(() => {
        refetchReports?.()
      }, POLL_INTERVAL_MS)
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current)
        pollRef.current = null
      }
    }
  }, [reports, refetchReports])

  const [downloadReport, { loading: downloadReportLoading }] = useDownloadReportLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,
    onError: handleGraphQLError,

    onCompleted(data) {
      const {
        downloadReport: { preSignedUrl, response }
      } = data || {}

      if (response?.status === 200) {
        if (preSignedUrl) {
          triggerFileDownload(preSignedUrl)
        } else {
          refetchReports && refetchReports()
          Alert.success('Report requested! You will receive an email when the report is ready.')
        }
      }
    }
  })

  const [cancelTargetId, setCancelTargetId] = useState<string | null>(null)

  const [retryReport, { loading: retryReportLoading }] = useRetryReportMutation({
    onCompleted(data) {
      if (data?.retryReport?.response?.status === 200) {
        Alert.success('Report retried. You will receive an email when it is ready.')
        refetchReports && refetchReports()
      }
    }
  })

  const [cancelReport, { loading: cancelReportLoading }] = useCancelReportMutation({
    onCompleted(data) {
      if (data?.cancelReport?.response?.status === 200) {
        Alert.success('Report cancelled.')
        refetchReports && refetchReports()
      }
    }
  })

  const handleRetry = (id: string) => {
    if (id) {
      retryReport({ variables: { reportId: id } })
    }
  }

  const handleConfirmCancel = () => {
    if (cancelTargetId) {
      cancelReport({ variables: { reportId: cancelTargetId } })
      setCancelTargetId(null)
    }
  }

  const handleGetReport = async (id: string, fileType: ReportFileType) => {
    if (id) {
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone

      downloadReport({
        variables: {
          reportDownloadInput: {
            id,
            fileType,
            userTimezone
          }
        }
      })
    }
  }

  const triggerFileDownload = async (preSignedUrl: string) => {
    try {
      setIsLoading(true)
      const response = await fetch(preSignedUrl)
      const contentLength = Number(response.headers.get('content-length') || 0)

      // check if the report is large than 300kb to print on screen
      if (previewReport && contentLength > 300000) {
        Alert.error('Report is too large to print on screen. Please download as CSV.')
        setPreviewReport(false)
        return
      }

      if (!response.ok) {
        Alert.error('Network response was not ok')
        return
      }

      if (previewReport) {
        await parseCsvDataToJson(response)
          .then(data => {
            setReportJsonData(data as ReportPreviewData[])
          })
          .catch(() => {
            Alert.error('Something went wrong')
          })
        return
      }

      const blob = await response.blob()
      const urlParts = preSignedUrl.split('?')
      const pathParts = urlParts[0].split('/')
      const originalFileName = decodeURIComponent(pathParts[pathParts.length - 1])

      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = originalFileName
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      Alert.error('Failed to download file')
    } finally {
      setIsLoading(false)
    }
  }

  const parseCsvDataToJson = async (response: Response) => {
    if (!response.ok) {
      Alert.error('Network response was not ok')
      return
    }

    const text = await response.text()
    const parsed = Papa.parse(text, { header: true })
    return parsed.data
  }

  const handleChangeSearch = (name: string, value: string) => {
    searchParams.set(name, value)
    searchParams.set('currentPage', currentPage.toString())
    setSearchParams(searchParams)
  }

  const resetSearchParams = () => {
    searchParams.delete('reportGroup')
    searchParams.delete('reportSelection')
    searchParams.delete('currentPage')
    searchParams.delete('startDate')
    searchParams.delete('endDate')
    // Reset returns the user filter to its default (own reports) rather than clearing it
    // to "All" — deleting the param lets the seeding effect re-apply the logged-in user.
    searchParams.delete(QUERY_PARAMS.USER)
    setSearchParams(searchParams)
  }

  const onCloseReportPreviewDialog = () => {
    setPreviewReport(false)
    setReportJsonData([])
  }

  useEffect(() => {
    // Hold off until the URL carries a user, otherwise the first render fires an
    // unfiltered request that flashes everyone's reports before the seeding effect lands.
    if (!creatorId) return

    fetchReports({
      variables: {
        input: {
          paginationOptions: {
            page: currentPage,
            limit: REPORTS_PAGE_LIMIT
          },
          reportGroup: searchParams.get('reportGroup'),
          reportSelection: searchParams.get('reportSelection'),
          startDate: startDate,
          endDate: endDate || dayjs().format('YYYY-MM-DD'),
          // 'all' is the sentinel the shared UserFilter emits; the API treats an omitted
          // creatorId as "every user", so it must not be sent through as a literal id.
          creatorId: isAllUsers ? null : creatorId
        }
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, startDate, endDate, reportGroup, reportSelection, creatorId, isAllUsers])

  const isEmpty = reports?.length === 0 && !fetchReportsLoading

  return (
    <Fragment>
      <ReportWidget reportTemplates={reportTemplates} refetch={refetchReports} />

      <FormProvider {...methods}>
        <Box display="flex" flexDirection="column" mt={2}>
          <CardBox marginTop={3}>
            <ContentHead title="Report History" />

            <Stack direction="row" spacing={2} m={2} alignItems="center">
              <Box flex={1} display="flex" gap={2} alignItems="center">
                <Box width="100%" maxWidth={200}>
                  <ReportGroupSelect
                    reportTemplates={reportTemplates}
                    value={reportGroup || ''}
                    onChange={value => {
                      searchParams.delete('reportSelection')
                      handleChangeSearch('reportGroup', value)
                    }}
                  />
                </Box>
                {/* Wider than the group filter: report labels are long
                    (e.g. "Timekeeper Analysis Matter Detail/PR4A"). */}
                <Box width="100%" maxWidth={400}>
                  <ReportSelectionSelect
                    reportTemplates={reportTemplates}
                    value={reportSelection || ''}
                    onChange={value => handleChangeSearch('reportSelection', value)}
                    groupId={reportGroup || ''}
                  />
                </Box>
                <Box width="100%" maxWidth={220}>
                  {/* Shown but locked for a restricted user: their history only ever contains their
                      own reports, so the filter stays visible as a statement of what is being
                      listed rather than a control they can widen. */}
                  <ReportCreatorSelect
                    value={creatorId || ''}
                    onChange={value => handleChangeSearch(QUERY_PARAMS.USER, value)}
                    disabled={isRestricted}
                  />
                </Box>
                <Box flex={1}>
                  <DateRangeFilter
                    fromDate={startDate}
                    toDate={endDate}
                    onFromDateChange={date => handleDateChange(date, endDate)}
                    onToDateChange={date => handleDateChange(startDate, date)}
                    dateFormat="MM/DD/YYYY"
                    filterDateFormat="MM/DD/YYYY"
                  />
                </Box>

                <Box flex={1}>
                  <Tooltip title="Reset all filters" placement="top">
                    {
                      <IconButton color="primary" onClick={resetSearchParams}>
                        <FilterAltOffIcon />
                      </IconButton>
                    }
                  </Tooltip>
                </Box>
              </Box>
            </Stack>

            {isEmpty ? (
              <Box sx={{ ...flexCenter, height: 'calc(100vh - 400px)' }}>
                <EmptyData title={'No reports to show!'} />
              </Box>
            ) : (
              <StickyTable pagination={<CustomPagination loading={false} />} height="405px">
                <TableHead>
                  <TableRow hover>
                    {REPORT_HEADERS.map((columnName, index) => (
                      <TableCell key={index} component="th" align={'left'}>
                        {columnName}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {reports?.map(report => {
                    const {
                      id,
                      createdAt,
                      creator,
                      reportGroup,
                      reportSelection,
                      status,
                      selectionFields,
                      displaySelectionFields
                    } = report || {}
                    return (
                      <TableRow key={id} hover>
                        <CustomTableCell value={formatDateTime(createdAt)} />
                        <CustomTableCell value={`${creator?.firstName} ${creator?.lastName}`} />
                        <CustomTableCell value={reportGroup || '-'} />
                        {/* Labels like "Timekeeper Analysis Matter Detail/PR4A" do not fit
                            on one line — wrap instead of truncating. */}
                        <CustomTableCell
                          value={(reportSelection && selectionLabelsByValue[reportSelection]) || reportSelection || '-'}
                          noWrap={false}
                        />
                        <TableCell>
                          <Box
                            sx={{ fontSize: '0.86rem', color: 'text.secondary', maxWidth: '220px' }}
                            dangerouslySetInnerHTML={{
                              // Passed straight through: the resolver returns null for a report
                              // stored without selection fields, and getSelectionFieldEntries
                              // already handles null. The Object.fromEntries(Object.entries(…))
                              // that used to wrap this was a no-op clone that threw on those rows
                              // and took the whole history table down with it.
                              __html: getSelectionFieldEntries(displaySelectionFields)
                                .map(({ label, value }) => `<b>${label}:</b> ${value}`)
                                .join('<br/>')
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: 'inline-block',
                              padding: '4px 12px',
                              borderRadius: '16px',
                              border: `1px solid`,
                              color: statusColors[status as ReportStatus]
                            }}
                          >
                            {status}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{ fontSize: '0.86rem', color: 'text.secondary', maxWidth: '250px' }}
                            dangerouslySetInnerHTML={{
                              __html: formatSelectionFields(selectionFields, reportGroup, reportSelection)
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          {status && (
                            <ReportsMenu
                              reportId={id!}
                              status={status}
                              refetch={refetchReports}
                              onPrint={async () => {
                                await handleGetReport(id!, ReportFileType.Csv)
                                setSelectedReport(report as Report)
                                setPreviewReport(true)
                              }}
                              onDownloadCSV={() => handleGetReport(id!, ReportFileType.Csv)}
                              onDownloadPDF={() => handleGetReport(id!, ReportFileType.Pdf)}
                              onRetry={() => handleRetry(id!)}
                              onCancel={() => setCancelTargetId(id!)}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </StickyTable>
            )}
          </CardBox>
        </Box>
      </FormProvider>

      {(downloadReportLoading || isLoading || retryReportLoading || cancelReportLoading) && <OverlayLoader />}

      <ConfirmationModal
        isOpen={!!cancelTargetId}
        title="Cancel Report"
        message="Are you sure you want to cancel this report? This will stop it from being processed and cannot be undone."
        confirmText="Cancel Report"
        cancelText="Keep Report"
        variant="danger"
        isLoading={cancelReportLoading}
        onConfirm={handleConfirmCancel}
        onCancel={() => setCancelTargetId(null)}
      />

      {!!reportJsonData?.length && (
        <ReportPreviewDialog
          open={previewReport}
          onClose={onCloseReportPreviewDialog}
          data={reportJsonData}
          reportGroup={selectedReport?.reportGroup || ''}
          reportSelection={selectedReport?.reportSelection || ''}
          selectionFields={selectedReport?.displaySelectionFields || {}}
          generatedDate={selectedReport?.createdAt || ''}
        />
      )}
    </Fragment>
  )
}

export default Reports
